import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { Guid } from "@/Common/Models/Guid";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as operations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { IDocumentsTabModel } from "@/Documents/Models/UI/IDocumentsTabModel";

interface IDocumentSettingsContainerProps {
    Model: IDocumentsTabModel;
}

class DocumentSettingsContainerState {
    public initialState: IDocumentsTabModel;
    public HrmShares: Guid[] = [];
}

class DocumentSettingsContainer extends BaseReactComponent<IDocumentSettingsContainerProps, DocumentSettingsContainerState> {
    constructor(props: IDocumentSettingsContainerProps) {
        super(props);
        this.state = { initialState: props.Model, HrmShares: [] };
    }

    public async loadHrmDocumentSharedWithMe(): Promise<void> {
        const response = await Client.postAsync<{ HrmDocumentShares: Guid[] }>(
            operations.Server.Hrm_GetHrmSharesWithMe,
            null
        );

        const shares = response.data.HrmDocumentShares;

        this.mutateState(state => state.HrmShares = shares);
    }

    public componentDidMount(): void {
        super.setOverlay("DocumentSettingsContainer", { transparent: true });
        this.loadHrmDocumentSharedWithMe();
    }

    public render(): JSX.Element {
        let str = this.props.Model.get("CliniciansSearchText").trim();

        let cliniciansToRender = this.props.Model.get("SelectedView") === "deactivatedAccount"
            ? this.FilterText(str, this.props.Model.get("DeactivatedAccountCliniciansCache"))
            : this.FilterText(str, this.props.Model.get("CliniciansGlobalCache"));

        if (cacheStore.ClinicSettings.EnableHrmDocumentProtection) {
            cliniciansToRender = this.props.Model
                .get("CliniciansGlobalCache")
                .filter(p => [cacheStore.GetCurrentEmployee().Contact_id, ...this.state.HrmShares].contains(p.ContactId))
                .toList();
        }

        let strSentBy = this.props.Model.get("SentBySearchText").trim();
        let sentByToRender = this.FilterText(strSentBy, this.props.Model.get("SentByGlobalCache"));
        let hasPortalAccess = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;
        const maxMinHeightStyle: React.CSSProperties = { height: "400px" };
        const shouldComponentAlphabeticallyReSortStatuses = !cacheStore.ClinicSettings.EnableApprovedByColleagueDocumentStatus;

        return (
            <div>
                <table className="f-content">
                    <thead>
                        <tr>
                            <th className="first">
                                <label style={{ display: "inline" }}>{cacheStore.GetString("DateSentLabel").toUpperCase()}</label>
                            </th>
                            <th>
                                {this.props.Model.get("Sites").count() > 1 && (
                                    <CheckboxControl
                                        id="sites_check"
                                        dataCy="document-settings-allSites"
                                        label={cacheStore.GetString("SitesLabel").toUpperCase()}
                                        operation={LocalOperations.Documents.Checkbox_Change}
                                        checked={this.props.Model.get("SelectAllSitesChecked")}
                                        target={CheckboxTarget.Myle_Documents_SitesControlCheck}
                                    />
                                )}
                            </th>
                            <th>
                                <CheckboxControl
                                    id="clinicians_check"
                                    dataCy="document-settings-allClinicians"
                                    label={cacheStore.GetString("CliniciansLabel").toUpperCase()}
                                    operation={LocalOperations.Documents.Checkbox_Change}
                                    checked={this.props.Model.get("SelectAllCliniciansChecked")}
                                    target={CheckboxTarget.Myle_Documents_CliniciansControlCheck}
                                    idModifier="clinicians"
                                />
                            </th>
                            {!this.props.Model.get("SelectedDocumentSubclass") && (
                                <th>
                                    <CheckboxControl
                                        id="documents_class_check"
                                        label={cacheStore.GetString("DocumentClassLabel").toUpperCase()}
                                        operation={LocalOperations.Documents.Checkbox_Change}
                                        checked={this.props.Model.get("SelectAllClassesChecked")}
                                        target={CheckboxTarget.Myle_Documents_ClassControlCheck}
                                    />
                                </th>
                            )}
                            <th>
                                <CheckboxControl
                                    id="status_check"
                                    dataCy="document-settings-status"
                                    label={cacheStore.GetString("StatusLabel").toUpperCase()}
                                    operation={LocalOperations.Documents.Checkbox_Change}
                                    checked={this.props.Model.get("SelectAllStatusChecked")}
                                    target={CheckboxTarget.Myle_Documents_StatusControlCheck}
                                />
                            </th>

                            <th className={!hasPortalAccess ? "last" : ""}>
                                <CheckboxControl
                                    id="urgency_check"
                                    label={cacheStore.GetString("UrgencyLabel").toUpperCase()}
                                    operation={LocalOperations.Documents.Checkbox_Change}
                                    checked={this.props.Model.get("SelectAllUrgencyChecked")}
                                    target={CheckboxTarget.Myle_Documents_UrgencyControlCheck}
                                />
                            </th>

                            {hasPortalAccess && (
                                <th>
                                    <CheckboxControl
                                        id="sentBy_check"
                                        label={cacheStore.GetString("SentByLabel").toUpperCase()}
                                        operation={LocalOperations.Documents.Checkbox_Change}
                                        checked={this.props.Model.get("SelectAllSentByChecked")}
                                        target={CheckboxTarget.Myle_Documents_SentByControlCheck}
                                    />
                                </th>
                            )}

                            {hasPortalAccess && (
                                <th className="last">
                                    <CheckboxControl
                                        id="statusportal_check"
                                        label={cacheStore.GetString("StatusPortal").toUpperCase()}
                                        operation={LocalOperations.Documents.Checkbox_Change}
                                        checked={this.props.Model.get("SelectAllPortalStatus")}
                                        target={CheckboxTarget.Myle_Documents_PortalStatusControlCheck}
                                        idModifier="sentby"
                                    />
                                </th>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            <td className="first">
                                <MultiDateSelector
                                    id="doc_search_date_selector"
                                    dataCy="document-settings-date"
                                    fromMyleOperation={LocalOperations.Documents.SearchSettings_FromDateChanged}
                                    toMyleOperation={LocalOperations.Documents.SearchSettings_ToDateChanged}
                                    fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    from={this.props.Model.get("DateFrom")}
                                    to={this.props.Model.get("DateTo")}
                                    errorMessage={this.props.Model.get("ValidationError")}
                                />
                            </td>

                            <td className="sites-column" style={{ maxWidth: "256px" }}>
                                <div style={this.props.Model.get("Sites").count() === 1 ? { display: "none" } : maxMinHeightStyle}>
                                    {this.props.Model.get("Sites").count() > 1 && (
                                        <div>
                                            <CheckboxControl
                                                id="sites_onlySelected"
                                                dataCy="document-settings-onlySelectedSites"
                                                label={cacheStore.GetString("ShowOnlySelectedSiteLabel")}
                                                operation={LocalOperations.Documents.Checkbox_Change}
                                                checked={this.props.Model.get("ShowOnlySelectedSites")}
                                                target={CheckboxTarget.Myle_Documents_OnlySelectedSites}
                                                DivWrap={true}
                                                wrapSpan={true}
                                            />
                                            <div style={{ ...maxMinHeightStyle, overflowY: "scroll" }}>
                                                <CheckedListControl
                                                    dataCy="document-settings-site"
                                                    DataSource={this.props.Model.get("Sites")}
                                                    Operation={LocalOperations.Documents.Checkbox_Change}
                                                    MaxTextLength={25}
                                                    onChange={() => sessionStore.dispatch(LocalOperations.Documents.Show_OnlySelectedSites)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </td>

                            <td style={{ maxWidth: "227px", overflow: "auto" }}>
                                <input
                                    data-cy="document-settings-clinicianSearch"
                                    className="form-control"
                                    type="text"
                                    value={this.props.Model.get("CliniciansSearchText")}
                                    onChange={e => this.onCliniciansFilterTextChanged(e)}
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div style={{ height: "400px" }}>
                                    <CheckedListControl
                                        dataCy="document-settings-clinician"
                                        DataSource={cliniciansToRender}
                                        Operation={LocalOperations.Documents.Checkbox_Change}
                                        sort={true}
                                        limitTextWidth={!cacheStore.ClinicSettings.EnableLazyRenderingFilterSettingsClinicians}
                                        idModifier="clinicians"
                                        lazyRenderingContainerWidth={219}
                                        enableLazyRendering
                                    />
                                </div>
                            </td>
                            {!this.props.Model.get("SelectedDocumentSubclass") && (
                                <td className="white-space-no-wrap">
                                    <CheckedListControl DataSource={this.props.Model.get("Classes")} resolveValueInStringChange={false} Operation={LocalOperations.Documents.Checkbox_Change} />
                                </td>
                            )}
                            <td className="white-space-no-wrap">
                                <CheckedListControl
                                    sort={shouldComponentAlphabeticallyReSortStatuses}
                                    DataSource={this.props.Model.get("Status")}
                                    resolveValueInStringChange={true}
                                    Operation={LocalOperations.Documents.Checkbox_Change}
                                />
                            </td>
                            <td className={`white-space-no-wrap ${!hasPortalAccess ? "last" : ""}`}>
                                <CheckedListControl DataSource={this.props.Model.get("Urgency")} resolveValueInStringChange={true} Operation={LocalOperations.Documents.Checkbox_Change} />
                            </td>

                            {hasPortalAccess && (
                                <td style={{ maxWidth: "227px", overflow: "auto" }}>
                                    <input
                                        className="form-control"
                                        type="text"
                                        value={this.props.Model.get("SentBySearchText")}
                                        onChange={e => this.onSentByFilterTextChanged(e)}
                                        placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                    />
                                    <div style={{ height: "400px" }}>
                                        <CheckedListControl
                                            DataSource={sentByToRender}
                                            Operation={LocalOperations.Documents.Checkbox_Change}
                                            sort={true}
                                            limitTextWidth={!cacheStore.ClinicSettings.EnableLazyRenderingFilterSettingsClinicians}
                                            idModifier="sentBy"
                                            lazyRenderingContainerWidth={219}
                                            enableLazyRendering
                                        />
                                    </div>
                                </td>
                            )}

                            {hasPortalAccess && (
                                <td className="last">
                                    <CheckedListControl DataSource={this.props.Model.get("PortalStatuses")} resolveValueInStringChange={true} Operation={LocalOperations.Documents.Checkbox_Change} limitTextWidth={true} idModifier="portalStatus" />
                                </td>
                            )}
                        </tr>
                    </tbody>
                </table>
                <SearchSettingsFooter
                    onClose={() => this.onCancelButtonClick()}
                    onOk={() => this.onOKButtonClick()}
                    onColumnOrderingButtonClick={() => sessionStore.dispatch(LocalOperations.Documents.DisplaySettings_ShowColumOrderPopupClick)}
                    onResultsPerPageChange={value => sessionStore.dispatch(LocalOperations.Documents.SearchSettings_OnResultsPerPageChange, { value })}
                    selectedPageSizeOption={this.props.Model.get("SelectedPageSizeOption")}
                    pageSizeOptions={this.props.Model.get("PageSizeOptions").toArray()}
                    onSaveSettingsCheckboxChange={checked => this.setIsSaveSettingsChecked(checked)}
                    isSaveSettingsChecked={this.props.Model.get("SaveDisplaySettings")}
                    disableOkButton={this.getIsFormInvalid()}
                />
            </div>
        );
    }

    private getIsFormInvalid(): boolean {
        return !!this.props.Model.get("ValidationError");
    }

    private FilterText(str: string, fullList: Immutable.List<EmployeeUIModel>): Immutable.List<EmployeeUIModel> {
        let q = fullList;
        if (str !== "") { q = SearchMenuHelper.FilterCliniciansByName(str, fullList); }
        return q;
    }

    private setIsSaveSettingsChecked(saveSettingChecked: boolean): void {
        sessionStore.dispatch(
            LocalOperations.Documents.Checkbox_Change,
            {
                target: CheckboxTarget.Myle_Documents_SaveSettingsCheck,
                checked: saveSettingChecked
            }
        );
    }

    public onCliniciansFilterTextChanged(event: React.ChangeEvent<HTMLInputElement>): void {
        let operation = new LocalEvent(LocalOperations.Documents.SearchSettings_ClinicianFilterTextChanged);
        operation.AddItem("value", event.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    public onSentByFilterTextChanged(event: React.ChangeEvent<HTMLInputElement>): void {
        let operation = new LocalEvent(LocalOperations.Documents.SearchSettings_SentByFilterTextChanged);
        operation.AddItem("value", event.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    public onOKButtonClick(): void {
        sessionStore.dispatch(LocalOperations.Documents.SearchSettings_OnOkClick);
    }

    public onCancelButtonClick(): void {
        sessionStore.dispatch(LocalOperations.Documents.SearchSettings_OnCancelClick, { initialState: this.state.initialState });
    }
}

export { DocumentSettingsContainer };
